import { supabase } from "@/shared/lib/supabase";
import { trackEvent, EventType } from "@/shared/utils/core";
import { logActivity } from "@/shared/utils/activityLogger";
import { notifyInvitedMemberJoined } from "@/shared/utils/slackNotifications";

async function getContactAddress(estateId: number, email: string): Promise<string | null> {
  const { data } = await supabase
    .from("estate_contacts")
    .select("address")
    .eq("estate_id", estateId)
    .eq("email", email.toLowerCase())
    .single();

  return data?.address ?? null;
}

export async function acceptInvitation(
  token: string,
  email: string,
  relationship: string,
): Promise<boolean> {
  const {
    data: { user: authUser },
  } = await supabase.auth.getUser();
  if (!authUser) return false;

  const { data: userData } = await supabase
    .from("users")
    .select("user_id, first_name, last_name, phone_number")
    .eq("auth_id", authUser.id)
    .single();

  if (!userData) return false;

  const { data: invitation } = await supabase
    .from("estate_invitations")
    .select("id, estate_id, person:estates(full_name)")
    .eq("invitation_token", token)
    .single();

  if (!invitation) return false;

  const estateId = invitation.estate_id;

  const { error: userPersonError } = await supabase.from("user_estates").insert({
    user_id: userData.user_id,
    estate_id: estateId,
    relationship_to_decedent: relationship || null,
  });

  if (userPersonError) {
    console.error("Error adding user to person:", userPersonError);
    return false;
  }

  const contactAddress = await getContactAddress(estateId, email);

  await supabase
    .from("users")
    .update({
      last_active_estate_id: estateId,
      completed_onboarding: true,
      ...(contactAddress ? { address: contactAddress } : {}),
    })
    .eq("user_id", userData.user_id);

  await supabase
    .from("estate_invitations")
    .update({ status: "accepted" })
    .eq("invitation_token", token);

  await supabase
    .from("estate_contacts")
    .update({
      user_id: userData.user_id,
      relationship,
      ...(userData.first_name ? { first_name: userData.first_name } : {}),
      ...(userData.last_name ? { last_name: userData.last_name } : {}),
      ...(userData.phone_number ? { phone: userData.phone_number } : {}),
    })
    .eq("estate_id", estateId)
    .eq("email", email.toLowerCase());

  trackEvent(EventType.INVITE_ACCEPTED, {
    flow: "family_invite",
    invite_method: "email_signup",
    estate_id: String(estateId),
  });

  logActivity({
    estate_id: estateId,
    action_type: "accepted_invitation",
    user_id: userData.user_id,
    entity_type: "invitation",
    entity_id: String(invitation.id),
    metadata: {
      email: email.toLowerCase(),
      relationship: relationship || null,
      invite_method: "email_signup",
    },
  }).catch(() => {});

  notifyInvitedMemberJoined(estateId, relationship).catch(() => {});

  return true;
}

export async function acceptLawyerInvitation(token: string): Promise<boolean> {
  const {
    data: { user: authUser },
  } = await supabase.auth.getUser();
  if (!authUser) return false;

  const { data: userData } = await supabase
    .from("users")
    .select("user_id")
    .eq("auth_id", authUser.id)
    .single();

  if (!userData) return false;

  const { data, error } = await supabase.functions.invoke("account/accept-lawyer-invitation", {
    body: { token },
  });

  if (error || !data?.success) {
    console.error("Error linking lawyer to estate:", error || data?.error);
    return false;
  }

  const estateId = data.estateId as number;
  const invitationId = data.invitationId as number;

  const { error: updateError } = await supabase
    .from("estate_invitations")
    .update({ status: "accepted" })
    .eq("invitation_token", token);

  if (updateError) {
    console.error("Failed to update invitation status:", updateError.message);
    return false;
  }

  trackEvent(EventType.INVITE_ACCEPTED, {
    invite_method: "attorney_signup",
    estate_id: estateId,
  });

  logActivity({
    estate_id: estateId,
    action_type: "accepted_invitation",
    user_id: userData.user_id,
    entity_type: "invitation",
    entity_id: String(invitationId),
    metadata: {
      invite_method: "attorney_signup",
    },
  }).catch(() => {});

  return true;
}

export async function acceptInvitationForExistingUser(
  token: string,
  email: string,
): Promise<boolean> {
  const {
    data: { user: authUser },
  } = await supabase.auth.getUser();
  if (!authUser) return false;

  const { data: userData } = await supabase
    .from("users")
    .select("user_id, first_name, last_name, phone_number")
    .eq("auth_id", authUser.id)
    .single();

  if (!userData) return false;

  const { data: invitation } = await supabase
    .from("estate_invitations")
    .select("id, estate_id, person:estates(full_name)")
    .eq("invitation_token", token)
    .single();

  if (!invitation) return false;

  const estateId = invitation.estate_id;

  const { error: userPersonError } = await supabase
    .from("user_estates")
    .insert({ user_id: userData.user_id, estate_id: estateId });

  if (userPersonError) {
    console.error("Error adding user to person:", userPersonError);
    return false;
  }

  const contactAddress = await getContactAddress(estateId, email);

  await supabase
    .from("users")
    .update({
      last_active_estate_id: estateId,
      completed_onboarding: true,
      ...(contactAddress ? { address: contactAddress } : {}),
    })
    .eq("user_id", userData.user_id);

  await supabase
    .from("estate_invitations")
    .update({ status: "accepted" })
    .eq("invitation_token", token);

  await supabase
    .from("estate_contacts")
    .update({
      user_id: userData.user_id,
      ...(userData.first_name ? { first_name: userData.first_name } : {}),
      ...(userData.last_name ? { last_name: userData.last_name } : {}),
      ...(userData.phone_number ? { phone: userData.phone_number } : {}),
    })
    .eq("estate_id", estateId)
    .eq("email", email.toLowerCase());

  trackEvent(EventType.INVITE_ACCEPTED, {
    flow: "family_invite",
    invite_method: "existing_user_login",
    estate_id: String(estateId),
  });

  logActivity({
    estate_id: estateId,
    action_type: "accepted_invitation",
    user_id: userData.user_id,
    entity_type: "invitation",
    entity_id: String(invitation.id),
    metadata: {
      email: email.toLowerCase(),
      invite_method: "existing_user_login",
    },
  }).catch(() => {});

  notifyInvitedMemberJoined(estateId, "existing_user").catch(() => {});

  return true;
}

export async function acceptClientInvitation(
  token: string,
  email: string,
  userDetails?: { firstName: string; lastName: string; phoneNumber: string },
): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke("account/accept-client-invite", {
    body: {
      token,
      email: email.toLowerCase(),
      ...(userDetails ?? {}),
    },
  });

  if (error || !data?.success) {
    console.error("Error accepting client invitation:", error ?? data);
    return false;
  }

  trackEvent(EventType.INVITE_ACCEPTED, {
    flow: "attorney_invite",
    invite_method: "attorney_client",
    estate_id: String(data.estateId),
  });

  return true;
}
